/**
 * Created: 2026-10-01
 * Initial commit: Add customer payment statistics page
 *
 * Lists every payment and refund a chosen customer made at the current site in
 * a date range, with the net total in the grid footer. All three filters (start
 * date, end date, customer) are required before a search can run. Clicking a
 * row opens the related order.
 */

import { gql } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react'
import { Alert, Box, Button, Chip, Typography } from '@mui/material'
import {
  DataGridPro,
  type GridColDef,
  GridFooterContainer,
  type GridRowParams,
} from '@mui/x-data-grid-pro'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { ORDER_TYPE_LABEL } from '@tc-app/shared/labels'
import { endOfDay, startOfDay, startOfMonth } from 'date-fns'
import { useState } from 'react'
import OrderDialog from '../components/OrderDialog'
import UserSelector, { type UserSelectorUser } from '../components/UserSelector'
import {
  CustomerPaymentKind,
  CustomerPaymentsDocument,
  type CustomerPaymentsQuery,
  type OrderCategory,
} from '../gql/graphql'
import { useRole } from '../hooks'

gql`
  query CustomerPayments(
    $siteId: UUID!
    $customerId: UUID!
    $startDate: DateTime!
    $endDate: DateTime!
  ) {
    customerPayments(
      siteId: $siteId
      customerId: $customerId
      startDate: $startDate
      endDate: $endDate
    ) {
      id
      kind
      time
      amount
      orderId
      orderCategory
    }
  }
`

declare module '@mui/x-data-grid-pro' {
  interface FooterPropsOverrides {
    count: number
    total: number
  }
}

type PaymentRow = CustomerPaymentsQuery['customerPayments'][number]

const kindChipProps: Record<
  CustomerPaymentKind,
  { label: string; color: 'success' | 'error' }
> = {
  [CustomerPaymentKind.Payment]: { label: '支付', color: 'success' },
  [CustomerPaymentKind.Refund]: { label: '退款', color: 'error' },
}

/** Formats an amount in fen as a CNY string, e.g. -3000 → "-¥30.00". */
function formatFen(fen: number): string {
  const sign = fen < 0 ? '-' : ''
  return `${sign}¥${(Math.abs(fen) / 100).toFixed(2)}`
}

const columns: GridColDef<PaymentRow>[] = [
  {
    field: '__rowNumber',
    headerName: '#',
    width: 50,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
  },
  {
    field: 'time',
    headerName: '支付时间',
    width: 180,
    sortable: false,
    headerAlign: 'center',
    valueGetter: (value: string) => new Date(value).toLocaleString('zh-CN'),
  },
  {
    field: 'kind',
    headerName: '类型',
    width: 100,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: ({ row }) => {
      const chip = kindChipProps[row.kind]
      return <Chip label={chip.label} color={chip.color} size="small" />
    },
  },
  {
    field: 'orderCategory',
    headerName: '订单类型',
    width: 100,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    valueGetter: (value: OrderCategory) => ORDER_TYPE_LABEL[value] ?? value,
  },
  {
    field: 'orderId',
    headerName: '订单号',
    flex: 1,
    minWidth: 280,
    sortable: false,
    headerAlign: 'center',
  },
  {
    field: 'amount',
    headerName: '金额',
    width: 120,
    sortable: false,
    align: 'right',
    headerAlign: 'center',
    valueGetter: (value: number) => formatFen(value),
  },
]

function TotalFooter({ count, total }: { count: number; total: number }) {
  return (
    <GridFooterContainer sx={{ justifyContent: 'flex-end', gap: 2, px: 2 }}>
      <Typography variant="subtitle1">共 {count} 笔</Typography>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
        合计: {formatFen(total)}
      </Typography>
    </GridFooterContainer>
  )
}

export default function CustomerPaymentsPage() {
  const { currentSite } = useRole()
  const [startDate, setStartDate] = useState<Date | null>(startOfMonth(new Date()))
  const [endDate, setEndDate] = useState<Date | null>(new Date())
  const [selectedUser, setSelectedUser] = useState<UserSelectorUser | null>(null)
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null)

  const [execute, { data, loading, error }] = useLazyQuery(CustomerPaymentsDocument, {
    fetchPolicy: 'network-only',
  })

  const canSearch = Boolean(currentSite && startDate && endDate && selectedUser)

  const handleSearch = () => {
    if (!currentSite || !startDate || !endDate || !selectedUser) return
    execute({
      variables: {
        siteId: currentSite.id,
        customerId: selectedUser.id,
        startDate: startOfDay(startDate).toISOString(),
        endDate: endOfDay(endDate).toISOString(),
      },
    })
  }

  const rows = data?.customerPayments ?? []
  const total = rows.reduce((sum, r) => sum + r.amount, 0)

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h4">客户收支</Typography>
        <Box sx={{ flex: 1 }} />
        <DatePicker
          label="开始日期"
          value={startDate}
          onChange={setStartDate}
          format="yyyy-MM-dd"
          maxDate={endDate || undefined}
          slotProps={{
            textField: {
              size: 'small',
              sx: { width: 160 },
              required: true,
              slotProps: { inputLabel: { shrink: true } },
            },
          }}
        />
        <DatePicker
          label="结束日期"
          value={endDate}
          onChange={setEndDate}
          format="yyyy-MM-dd"
          minDate={startDate || undefined}
          maxDate={new Date()}
          slotProps={{
            textField: {
              size: 'small',
              sx: { width: 160 },
              required: true,
              slotProps: { inputLabel: { shrink: true } },
            },
          }}
        />
        <UserSelector
          siteId={currentSite?.id as string}
          value={selectedUser}
          onChange={setSelectedUser}
        />
        <Button
          variant="contained"
          size="small"
          onClick={handleSearch}
          disabled={!canSearch}
        >
          搜索
        </Button>
      </Box>

      {error && <Alert severity="error">加载收支记录失败: {error.message}</Alert>}

      <DataGridPro
        rows={rows}
        columns={columns}
        getRowId={(row: PaymentRow) => row.id}
        loading={loading}
        disableRowSelectionOnClick
        disableColumnMenu
        density="compact"
        onRowClick={(params: GridRowParams<PaymentRow>) =>
          setSelectedOrderId(params.row.orderId)
        }
        slots={{ footer: TotalFooter }}
        slotProps={{ footer: { count: rows.length, total } }}
        localeText={{ noRowsLabel: '暂无收支记录' }}
        sx={{
          flex: 1,
          minHeight: 0,
          '& .MuiDataGrid-row': { cursor: 'pointer' },
        }}
      />

      <OrderDialog orderId={selectedOrderId} onClose={() => setSelectedOrderId(null)} />
    </Box>
  )
}
